import { FunctionComponent } from "react"
import Sidebar from "../features/sidebar/components/sidebar"
import RecruiterUserList from "./recruiter-user-list"
import GroupsList from "./groups-list"
import WelcomePage from "./recruiter-admin/WelcomePage"
import MicrolearningSection from "./recruiter-admin/MicrolearningSection"
import TeamSimulationsSection from "./recruiter-admin/TeamSimulationsSection"
import WhoThinksSection from "./recruiter-admin/WhoThinksSection"
import MeasuresLibrarySection from "./recruiter-admin/MeasuresLibrarySection"
import AIIntegrationSection from "./recruiter-admin/AIIntegrationSection"
import PerformanceSection from "./recruiter-admin/PerformanceSection"
import BenchmarksSection from "./recruiter-admin/BenchmarksSection"
import { useLocation } from "react-router-dom"

const RecruiterAdminDashboard: FunctionComponent = () => {
  const location = useLocation()

  const renderContent = () => {
    const path = location.pathname

    // Welcome/Home page
    if (path === "/dashboard/recruiter-admin" || path === "/dashboard/recruiter-admin/welcome") {
      return <WelcomePage />
    }

    // ASSESS Group
    // Deploy Simulation (Users section under /users/ but NOT /manage/users)
    if (path.includes("/recruiter-admin/users") && !path.includes("/manage/")) {
      return (
        <div className="min-h-full bg-gray-50">
          <div className="px-8 pt-6 max-w-6xl">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Deploy Simulation</h1>
            <p className="text-base text-gray-500 mb-4">
              Launch a simulation validated by 500+ peer-reviewed publications. Select your measures, set a deadline, and send invitations—setup takes less than 10 minutes.
            </p>

            {/* Step-by-step guidance */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-0">
              <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">How it works</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="flex gap-2">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#6182F7] text-white flex items-center justify-center text-xs font-semibold">1</div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">Add Participants</h3>
                    <p className="text-xs text-gray-500">Add individually or upload a batch via CSV. Enter names, emails, and roles.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#6182F7] text-white flex items-center justify-center text-xs font-semibold">2</div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">Automatic Invitations</h3>
                    <p className="text-xs text-gray-500">New participants automatically receive an email invitation to complete their simulation.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#6182F7] text-white flex items-center justify-center text-xs font-semibold">3</div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">Send Reminders</h3>
                    <p className="text-xs text-gray-500">Send reminders to participants who have not started their assessment.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#6182F7] text-white flex items-center justify-center text-xs font-semibold">4</div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">Explore Results</h3>
                    <p className="text-xs text-gray-500">Review results through Cognitive Blueprints, Benchmarks, and Performance tabs.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <RecruiterUserList />
        </div>
      )
    }

    // Team Simulations section
    if (path.includes("/recruiter-admin/team-simulations")) {
      return <TeamSimulationsSection />
    }

    // REVIEW Group
    // Cognitive Blueprints (stacked bar chart of per-user measure scores)
    if (path.includes("/recruiter-admin/scores")) {
      return <PerformanceSection />
    }

    // Benchmarks
    if (path.includes("/recruiter-admin/benchmarks")) {
      return <BenchmarksSection />
    }

    // GROW Group
    // Develop (Microlearning)
    if (path.includes("/recruiter-admin/microlearning")) {
      return <MicrolearningSection />
    }

    // Who Thinks Like This? (formerly Assignment Match)
    if (path.includes("/recruiter-admin/who-thinks")) {
      return <WhoThinksSection />
    }

    // AI Integration
    if (path.includes("/recruiter-admin/ai-integration")) {
      return <AIIntegrationSection />
    }

    // MANAGE Group
    // Users (separate from Deploy Simulation)
    if (path.includes("/recruiter-admin/manage/users")) {
      return (
        <div className="min-h-full bg-gray-50">
          <div className="px-8 pt-8 max-w-6xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Users</h1>
            <p className="text-lg text-gray-500">
              Manage the people in your organization. Add team members, assign them to groups, and track their simulation and development status.
            </p>
          </div>
          <RecruiterUserList />
        </div>
      )
    }

    // Groups section
    if (path.includes("/recruiter-admin/groups")) {
      return (
        <div className="min-h-full bg-gray-50">
          <GroupsList />
        </div>
      )
    }

    // Measures Library section
    if (path.includes("/recruiter-admin/measures")) {
      return <MeasuresLibrarySection />
    }

    // Default to welcome page
    return <WelcomePage />
  }

  return (
    <div className="relative bg-white w-full h-[100dvh] flex flex-row items-start justify-start">
      <Sidebar />
      <div className="w-full h-[100dvh] flex-1 flex flex-col overflow-hidden">
        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-gray-50">
          {renderContent()}
        </div>
      </div>
    </div>
  )
}

export default RecruiterAdminDashboard
